import { FC } from 'react';
import { Button, ButtonGroup, Input, Spinner } from 'reactstrap';
import { useRecPage } from './context';

// Hardcoded approximations of the design reference's token values (--border,
// --gray-*, --brand) — this codebase has no CSS-variable design-system
// package to pull from (see README §Design Tokens vs. actual repo state).
const CARD_STYLE: React.CSSProperties = {
  background: '#fff',
  border: '1px solid #E2E5EA',
  borderRadius: 8,
  padding: 16,
  boxShadow: '0 1px 2px rgba(16,24,40,0.05)',
};

const SELECT_ROW_STYLE: React.CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 12,
};

const SUMMARY_ROW_STYLE: React.CSSProperties = {
  marginTop: 14,
  paddingTop: 14,
  borderTop: '1px solid #E2E5EA',
};

// kan-119 "Search in" row, under the job summary. Same divider treatment as the
// summary row so the card reads as three stacked sections.
const SCOPE_ROW_STYLE: React.CSSProperties = {
  marginTop: 12,
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: 10,
};

// Mirrors the proxy's id cap (packages/api recsys-proxy applicant pool): a
// pool larger than this is searched over its newest applications only.
const MAX_POOL_APPLICANTS = 8000;

const relativeDays = (createdAt: string | null | undefined): number | null => {
  if (!createdAt) return null;
  const created = new Date(createdAt).getTime();
  if (Number.isNaN(created)) return null;
  return Math.max(0, Math.floor((Date.now() - created) / 86400000));
};

const SelectionPanel: FC = () => {
  const {
    companies,
    jobs,
    jobsLoading,
    selectedCompanyId,
    jobId,
    activeJob,
    selectCompany,
    selectJob,
    poolScope,
    setPoolScope,
    poolInfo,
  } = useRecPage();

  const openDays = relativeDays(activeJob?.createdAt);
  const locationLabel = activeJob?.companyLocation
    ? [activeJob.companyLocation.city, activeJob.companyLocation.state].filter(Boolean).join(', ')
    : null;
  const department = activeJob?.category?.name ?? null;
  // "New applicants (N)": the job list's own count until an applicant-mode run
  // reports the pool size the proxy actually resolved (kan-119).
  const applicantCount = poolInfo ? poolInfo.applicants : Number(activeJob?.newApplications) || 0;
  const applicantMode = poolScope === 'applicants';
  // Re-clicking the active option is a no-op: setPoolScope would otherwise
  // clear the pool numbers and the results for nothing.

  return (
    <div style={CARD_STYLE}>
      <div style={SELECT_ROW_STYLE}>
        <div style={{ flex: '1 1 260px', minWidth: 220 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: '#667085', display: 'block', marginBottom: 4 }}>
            Client company
          </label>
          <Input
            type="select"
            value={selectedCompanyId ?? ''}
            onChange={(e) => selectCompany(e.target.value || null)}
            style={{ height: 38 }}
          >
            <option value="">Select a company…</option>
            {companies.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </Input>
        </div>

        <div style={{ flex: '1 1 260px', minWidth: 220 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: '#667085', display: 'block', marginBottom: 4 }}>
            Active job order
            {jobsLoading && <Spinner size="sm" style={{ marginLeft: 6, verticalAlign: 'text-bottom' }} />}
          </label>
          <Input
            type="select"
            value={jobId ?? ''}
            onChange={(e) => selectJob(e.target.value || null)}
            disabled={!selectedCompanyId || jobsLoading || jobs.length === 0}
            style={{ height: 38 }}
          >
            {jobsLoading && <option value="">Loading job orders…</option>}
            {!jobsLoading && jobs.length === 0 && <option value="">No active job orders</option>}
            {jobs.map((j) => {
              const loc = j.companyLocation
                ? [j.companyLocation.city, j.companyLocation.state].filter(Boolean).join(', ')
                : '';
              return (
                <option key={j.id} value={j.id}>
                  {j.jobTitle}
                  {loc ? ` — ${loc}` : ''}
                </option>
              );
            })}
          </Input>
        </div>
      </div>

      {activeJob && (
        <div style={SUMMARY_ROW_STYLE}>
          <div style={{ fontSize: 15, fontWeight: 600, color: '#1F2937' }}>
            {activeJob.jobTitle}
            {locationLabel ? (
              <span style={{ fontWeight: 500, color: '#667085' }}>{` · ${locationLabel}`}</span>
            ) : null}
          </div>
          <div style={{ fontSize: 13, color: '#667085', marginTop: 2 }}>
            {activeJob.company.name}
            {department ? ` · ${department}` : ''}
            {openDays !== null ? ` · open ${openDays}d` : ''}
            {Number(activeJob.newApplications) > 0 ? ` · ${activeJob.newApplications} new` : ''}
          </div>

          {/* kan-119: restrict every search tab to this job order's new
              applicants. Not persisted — resets to All on job change. */}
          <div style={SCOPE_ROW_STYLE}>
            <span style={{ fontSize: 12, fontWeight: 600, color: '#667085' }}>Search in:</span>
            <ButtonGroup size="sm" role="group" aria-label="Search in">
              <Button
                type="button"
                color={applicantMode ? 'secondary' : 'warning'}
                outline={applicantMode}
                aria-pressed={!applicantMode}
                onClick={() => {
                  if (applicantMode) setPoolScope('all');
                }}
              >
                All candidates
              </Button>
              <Button
                type="button"
                color={applicantMode ? 'warning' : 'secondary'}
                outline={!applicantMode}
                aria-pressed={applicantMode}
                onClick={() => {
                  if (!applicantMode) setPoolScope('applicants');
                }}
              >
                {`New applicants (${applicantCount})`}
              </Button>
            </ButtonGroup>
          </div>

          {applicantMode && poolInfo && (
            <div style={{ marginTop: 8 }}>
              <div style={{ fontSize: 12, color: '#667085' }}>
                {`${poolInfo.inCorpus} of ${poolInfo.applicants} applicants searchable`}
                {poolInfo.truncated ? ` · showing the newest ${MAX_POOL_APPLICANTS}` : ''}
              </div>
              {poolInfo.inCorpus < poolInfo.applicants && (
                <div style={{ fontSize: 12, color: '#98A2B3', marginTop: 2 }}>
                  Résumés are indexed overnight; applicants added today appear tomorrow.
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SelectionPanel;
